import React__default from 'react';
import { addressToString } from '../../../../utils/address_utils.js';
import { Text } from '../../../atoms/Text.js';
import CheckCircleFilled16 from '../../../icons/CheckCircleFilled16.js';
import XCircleFilled16 from '../../../icons/XCircleFilled16.js';
import './ReadOnlyFormField.scss';

/**
 * ReadOnlyField component - displays a single line with label - value.
 * Used when isReadOnly=true in FormField components.
 */
function ReadOnlyField({ label, value, description, required, className, }) {
    // Check if value is a boolean icon (React element)
    // For boolean types, formatReadOnlyValue returns CheckCircleFilled16 or XCircleFilled16
    const isBooleanIcon = React__default.isValidElement(value) &&
        typeof value !== 'string' &&
        typeof value !== 'number' &&
        (value.type === CheckCircleFilled16 ||
            value.type === XCircleFilled16 ||
            value.type?.name === 'CheckCircleFilled16' ||
            value.type?.name === 'XCircleFilled16');
    const displayValue = value ?? React__default.createElement("span", { className: 'read-only-field-placeholder--rev' }, "\u2014");
    return (React__default.createElement("div", { "data-rev-component": 'read-only-field', className: className },
        React__default.createElement("div", { className: 'read-only-field-content--rev' }, isBooleanIcon ? (React__default.createElement(React__default.Fragment, null,
            React__default.createElement("div", { className: 'read-only-field-row--rev' },
                React__default.createElement("span", { className: 'read-only-field-icon--rev' }, value),
                React__default.createElement(Text, { size: 'body', color: 'primary', className: 'read-only-field-label--rev', tag: 'span' },
                    label,
                    required && (React__default.createElement(Text, { size: 'body', color: 'tertiary', weight: 'regular', tag: 'span' },
                        ' ',
                        "(required)")))),
            description && (React__default.createElement(Text, { size: 'caption', color: 'secondary', className: 'read-only-field-description--rev', tag: 'div' }, description)))) : (React__default.createElement(React__default.Fragment, null,
            React__default.createElement("div", { className: 'read-only-field-row--rev' },
                label && (React__default.createElement(React__default.Fragment, null,
                    React__default.createElement("span", { className: 'read-only-field-label--rev' }, label),
                    required && (React__default.createElement(Text, { size: 'body', color: 'tertiary', weight: 'regular', tag: 'span' },
                        ' ',
                        "(required)")),
                    React__default.createElement("span", { className: 'read-only-field-separator--rev' }, " - "))),
                React__default.createElement("span", { className: 'read-only-field-value--rev' }, displayValue)),
            description && (React__default.createElement(Text, { size: 'caption', color: 'secondary', className: 'read-only-field-description--rev', tag: 'div' }, description)))))));
}
/**
 * Formats a value for display in read-only mode.
 */
function formatReadOnlyValue(value, type, options) {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    switch (type) {
        case 'checkbox':
        case 'switch':
            return value === true ? React__default.createElement(CheckCircleFilled16, null) : React__default.createElement(XCircleFilled16, null);
        case 'dropdown':
            if (options?.dropdownOptions) {
                const option = options.dropdownOptions.find((opt) => opt.id === value);
                return option?.label ?? value;
            }
            return value;
        case 'radio':
            if (options?.radioOptions) {
                const option = options.radioOptions.find((opt) => opt.value === value);
                return option?.label ?? value;
            }
            return value;
        case 'date':
            if (value instanceof Date) {
                return value.toLocaleDateString();
            }
            if (typeof value === 'string') {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                    return date.toLocaleDateString();
                }
            }
            return value;
        case 'time':
            if (value instanceof Date) {
                return value.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            }
            if (typeof value === 'string') {
                // Try to parse as time
                const timeMatch = value.match(/(\d{1,2}):(\d{2})/);
                if (timeMatch) {
                    return value;
                }
            }
            return value;
        case 'address':
            if (typeof value === 'object' && value !== null) {
                return addressToString(value);
            }
            return value;
        case 'textarea':
            // For textarea, show as plain text (single line, no line breaks)
            if (typeof value === 'string') {
                return value.replace(/\n/g, ' ');
            }
            return value;
        case 'text':
        default:
            return String(value);
    }
}

export { ReadOnlyField, formatReadOnlyValue };
//# sourceMappingURL=ReadOnlyFormField.js.map
